
import { defineComponent, PropType } from 'vue'

import { ScaSortT } from '~/types/reportTypes'
import { RepositoryScaTargetReportItem } from '~/types/types'
import { getPackageManagerIcon } from '~/utils/dependencies'
import { extractHashId } from '~/utils/hashId'
import { shortenLargeNumber } from '~/utils/string'

type Target = Pick<
  RepositoryScaTargetReportItem,
  | 'id'
  | 'repository'
  | 'packageManager'
  | 'manifestPath'
  | 'lockfilePath'
  | 'criticalSeverityVulnerabilityCount'
  | 'highSeverityVulnerabilityCount'
  | 'mediumSeverityVulnerabilityCount'
  | 'lowSeverityVulnerabilityCount'
>

export default defineComponent({
  name: 'ScaTargetsTable',
  components: {
    ScaTargetsSort: () => import('./ScaTargetsSort.vue')
  },
  model: {
    prop: 'selectedSortFilter',
    event: 'sort-filter-updated'
  },
  props: {
    selectedSortFilter: {
      type: String as PropType<ScaSortT>,
      required: true
    },
    isWidget: {
      type: Boolean,
      default: false
    },
    linkedRows: {
      type: Boolean,
      default: false
    },
    scaTargetsList: {
      type: Array as PropType<Array<Target>>,
      required: true
    },
    routeProvider: {
      type: String,
      required: true
    },
    routeOwner: {
      type: String,
      required: true
    }
  },
  computed: {
    activeSortFilter: {
      get() {
        return this.selectedSortFilter
      },
      set(val: ScaSortT) {
        this.$emit('sort-filter-updated', val)
      }
    },
    totalSortFilters() {
      return {
        [ScaSortT.TOTAL_ASCENDING]: {
          label: 'TOTAL',
          icon: 'arrow-up',
          name: ScaSortT.TOTAL_ASCENDING
        },
        [ScaSortT.TOTAL_DESCENDING]: {
          label: 'TOTAL',
          icon: 'arrow-down',
          name: ScaSortT.TOTAL_DESCENDING
        }
      }
    }
  },
  methods: {
    getPackageManagerIcon,
    extractHashId,
    vulnerabilityCounts(target: Target) {
      const countArray = []

      if (target.criticalSeverityVulnerabilityCount)
        countArray.push({
          icon: 'severity-critical',
          count: shortenLargeNumber(target.criticalSeverityVulnerabilityCount),
          color: 'text-cherry'
        })

      if (target.highSeverityVulnerabilityCount)
        countArray.push({
          icon: 'severity-high',
          count: shortenLargeNumber(target.highSeverityVulnerabilityCount),
          color: 'text-amber'
        })

      if (target.mediumSeverityVulnerabilityCount)
        countArray.push({
          icon: 'severity-medium',
          count: shortenLargeNumber(target.mediumSeverityVulnerabilityCount),
          color: 'text-honey'
        })

      if (target.lowSeverityVulnerabilityCount)
        countArray.push({
          icon: 'severity-low',
          count: shortenLargeNumber(target.lowSeverityVulnerabilityCount),
          color: 'text-vanilla-400'
        })

      return countArray
    },
    getTotalVulnerabilities(target: Target): number {
      const total =
        (target.criticalSeverityVulnerabilityCount || 0) +
        (target.highSeverityVulnerabilityCount || 0) +
        (target.mediumSeverityVulnerabilityCount || 0) +
        (target.lowSeverityVulnerabilityCount || 0)

      return total
    },
    shortenLargeNumber
  }
})
